import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import Intersect from 'vue-intersect';
import debounce from 'lodash/debounce';
import { Subject, timer, merge, from, of, interval } from 'rxjs';
import {
    filter,
    switchMap,
    map,
    take,
    takeUntil,
    debounceTime,
    tap,
    catchError,
    partition,
} from 'rxjs/operators';

import {
    ACCOUNT_ROLES_NEW,
    IFRAME_EVENT_MESSAGE,
    VIEW_AND_EDIT_ROLE,
} from '@/utils/consts';

import Drawer, { LoadingMaskText } from '@/components/Dashboard/common/Drawer';
import { ActionButton } from '@components/Common/Dialog';
import SearchInput from '@components/Common/searchInput/SearchInput';
import RingLoader from '@/components/Common/RingLoader';
import EditBox from '@/components/Dashboard/edit/EditBox';
import InputField from '@components/Dashboard/edit/InputField';
import Dropdown from '@/components/Common/DropdownBox/defaultDropdown';
import SelectedBox from '@/components/Common/DropdownBox/selectedBox';
import CheckDropdown from '@/components/Common/DropdownBox/checkDropdown';
import Checkbox from '@/components/Common/Checkbox';
import DatePicker from '@/components/connection/DatePicker';
import FileUploadConnector, {
    getUploadCopyKeys,
    getUploadableFileTypes,
} from './FileUploadConnector';
import TeamspaceAccess from './TeamspaceAccess';

import PrevIcon from '@svgIcons/prev.svg';
import TrashIcon from '@svgIcons/trash.svg';
import Accordion from '@svgIcons/accordion_open.svg';
import CrownIcon from '@svgIcons/crown.svg';
import XIcon from '@svgIcons/close.svg';

import { useSearch } from '@/composable/useSearch';

import { AssetService } from '@/services';
import UploadAPI from '@/API/upload';

import {
    isValueExist,
    generateId,
    upperHead,
    checkOrigin,
    getErrorCodeFromServer,
    listIncludes,
    filterDeprecated,
} from '@/utils/utils';
import { commDateFormat } from '@/utils/timeUtils';

import connectionMixin from '@/mixins/connection.js';
import channelIconMixin from '@/mixins/channelIcons.js';

import * as S from './styles';
import CustomFieldPanel from '@/components/exportData/panel/CustomFieldPanel';
import { DISPLAY_CHANNEL_NAME_FIRST_CHANNELS } from '@/utils/dashboard';
import { gtm, GTM_EVENT_NAME } from '@/utils/tracker/googleTagManager';
import withHMR from '@/utils/withHMR';

const checkValidValue = R.ifElse(
    R.propEq('type', 'boolean'),
    R.prop('value'),
    R.propSatisfies(isValueExist, 'value')
);

const prependPublicMediaUrl = (url) => {
    return `${process.env.VUE_APP_HOSTNAME}/${url}`;
};

const PAGES = {
        CONNECTION_TYPES: 'CONNECTION_TYPES',
        CONNECTIONS: 'CONNECTIONS',
        FORM_CONNECTION: 'FORM_CONNECTION',
        FORM_DATASTREAM: 'FORM_DATASTREAM',
        DATASTREAM_TYPES: 'DATASTREAM_TYPES',
    },
    {
        CONNECTION_TYPES,
        CONNECTIONS,
        FORM_CONNECTION,
        DATASTREAM_TYPES,
        FORM_DATASTREAM,
    } = PAGES;

const showAuthLink = ({
    url,
    cb = () => {},
    cancelCb = () => {},
    skipPopupClosedCheck = false,
}) => {
    // must remove event listener at destroyed lifecycle
    window.addEventListener('message', cb, false);

    const popup =
        window.open(
            url,
            '_blank',
            `toolbar=no, menubar=no, width=1024, height=870, top=100, left=100`
        ) || {};

    const done$ = new Subject();

    // Skip popup.closed monitoring for COOP connectors
    // (Google, Facebook, etc. return true for popup.closed due to COOP headers)
    if (!skipPopupClosedCheck) {
        timer(700, 500)
            .pipe(
                map(() => popup.closed),
                tap((v) => console.log(v, 'closed signal from autn link')),
                filter(R.identity),
                take(1),
                tap((v) => console.log(v, 'taken closed signal from autn link')),
                takeUntil(
                    merge(
                        done$.pipe(
                            tap((v) => console.log(v, 'done$ from auth link'))
                        ),
                        timer(60 * 1000 * 5)
                    )
                )
            )
            .subscribe((e) => {
                console.log(e, 'subscribed cancel from auth link');
                return cancelCb();
            });
    }

    return () => {
        console.log('event removed from auth link');
        return window.removeEventListener('message', cb);
    };
};

const sortName = R.sortBy(R.compose(R.toLower, R.prop('name')));

const GOOGLE_ADS = 'internal_google';
const GOOGLE_ANALYTICS = 'internal_googleAnalytics';
const GOOGLE_ANALYTIVS4 = 'internal_googleAnalytics4';
const GOOGLE_SPREADSHEET = 'internal_googleSpreadSheet';
const GOOGLE_DV360 = 'internal_dv360';
const YOUTUBE_PUBLIC = 'internal_youtubePublic';
const BIG_QUERY = 'internal_bigQuery';
const GOOGLE_ANALYTICS_MCF = 'internal_googleAnalyticsMCF';
const QUORA = 'internal_quora';
const SHOPIFY = 'internal_shopify';

const GOOGLE_CONNECTORS = new Set([
    GOOGLE_ADS,
    GOOGLE_ANALYTICS,
    GOOGLE_ANALYTIVS4,
    GOOGLE_SPREADSHEET,
    GOOGLE_DV360,
    YOUTUBE_PUBLIC,
    BIG_QUERY,
    GOOGLE_ANALYTICS_MCF,
]);

const isOauthCOOPConnectors = listIncludes([QUORA, SHOPIFY]);

const logGtmConnectionComplete = (asset) => {
    if (!isValueExist(asset)) {
        return;
    }

    const type = asset.type || '';
    const isPremium = asset.isPremium || false;

    const eventPayload = {
        id: asset.id,
        name: asset.data?.displayName || asset.data?.name || '',
        connector_type: type,
    };

    if (DISPLAY_CHANNEL_NAME_FIRST_CHANNELS.includes(type)) {
        gtm.logEvent(
            GTM_EVENT_NAME.FLEXIBLE_CONNECTION_CONNECTED,
            eventPayload
        );
    } else if (!isPremium && !type.includes('adverity')) {
        gtm.logEvent(
            GTM_EVENT_NAME.SUCCESSFUL_ADVANCED_CONNECTION,
            eventPayload
        );
    } else if (isPremium) {
        gtm.logEvent(GTM_EVENT_NAME.PREMIUM_CONNECTOR_CONNECTED, eventPayload);
    }
};

// Normalize a defaultValue entry (a plain string, or a { value, display_name }
// object as BE returns for multi_choice) into a choice-shaped option. The
// dropdown renders labels via getChoiceDisplayName, which reads `display_name`.
const toChoiceOption = (entry) =>
    R.is(Object, entry)
        ? {
              ...entry,
              display_name: entry.display_name ?? entry.label ?? entry.value,
          }
        : { value: entry, display_name: entry };

const refinedInitialFormData = (options) => {
    return options.map((v) => {
        const isMultiChoice = v.type === 'multi_choice';
        const isChoice = v.type === 'choice';

        // Value derived from statically-provided choices (the isSelected flag).
        const selectedFromChoices = isMultiChoice
            ? v?.choices?.filter(R.prop('isSelected')) || []
            : isChoice
            ? v?.choices?.find(R.prop('isSelected'))?.value
            : undefined;

        // dynamicChoices fields have no choices at init time (fetched on
        // click), so isSelected can't be present — fall back to defaultValue
        // to prefill the select. The value is independent of the option list;
        // once the user opens the dropdown the fetched choices replace the
        // placeholder seeded below. handleDynamicChoice won't clobber a preset
        // value (it guards with !isValueExist(item.value)).
        const defaultForChoice = (() => {
            if (!isValueExist(v.defaultValue)) return undefined;
            if (isMultiChoice) {
                // multi value is an array of choice-shaped objects
                return (
                    Array.isArray(v.defaultValue)
                        ? v.defaultValue
                        : [v.defaultValue]
                ).map(toChoiceOption);
            }
            // single choice value is the plain string id
            return R.is(Object, v.defaultValue)
                ? v.defaultValue.value
                : v.defaultValue;
        })();

        const value = isMultiChoice
            ? (isValueExist(selectedFromChoices)
                  ? selectedFromChoices
                  : defaultForChoice) || []
            : isChoice
            ? (isValueExist(selectedFromChoices)
                  ? selectedFromChoices
                  : defaultForChoice) || ''
            : v.defaultValue || '';

        // Seed placeholder choices so SelectedBox can render a label before the
        // dynamic options are fetched (findDisplayValue looks them up in
        // `choices`). Only when none were provided and we have a prefilled value.
        const seededChoices = (() => {
            if (isValueExist(v.choices) || !isValueExist(value)) return undefined;
            if (isMultiChoice) return value; // already choice-shaped
            if (isChoice) return [toChoiceOption(value)];
            return undefined;
        })();

        return R.mergeLeft(
            {
                id: generateId(),
                value,
                ...(isChoice && { dropdown: false }),
                ...(seededChoices && { choices: seededChoices }),
            },
            v
        );
    });
};

const DashboardConnectionPanel = {
    name: 'DashboardConnectionPanel',
    mixins: [connectionMixin, channelIconMixin],
    setup() {
        const {
            lazyValue: searchTerm,
            setValue: setSearchTerm,
            filter,
        } = useSearch({
            lazy: true,
        });

        return {
            searchTerm,
            setSearchTerm,
            filterOptions: filter(R.prop('name')),
        };
    },
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String | Number,
        },
        streamItem: {
            type: Object,
            default() {
                return {};
            },
        },
        closeOutside: {
            type: Boolean,
            default: true,
        },
        placement: {
            type: String,
            default: 'right',
        },
        isConnectionPage: Boolean,
        width: String | Number,
        isQuickConnect: Boolean,
        predefinedSearchTerm: String,
        bodyStyle: {
            type: Object,
            default() {
                return {};
            },
        },
        isFromAdgen: {
            type: Boolean,
            default: false,
        },
    },
    watch: {
        predefinedSearchTerm: {
            handler(value) {
                this.setPredefinedSearchTerm(value);
            },
        },
    },
    data() {
        return {
            page: '',
            prevPage: undefined,
            loading: false,
            loadingMask: false,
            typePage: 0,
            formData: [],
            removeEventFn: R.identity,
            connectionTypes: [],
            datastreamTypes: [],
            connections: [],
            connectionId: undefined,
            datastreamTypeId: undefined,
            connectionTypeId: undefined,
            connectionTypesIds: [],
            mixinFlag: 'dashboard',
            formOptionOpened: true,
            haveAccess: false,
            connectionType: '',
            helpUrl: '',
            fieldToSplitDataSourceCreation: '',
            fileConnectorPanelOpened: false,
            generatedAssetId: '',
            generatedAssetType: '',
            isPremium: false,
            customFieldPanelOpend: false,
            customFieldPanelProps: {},
            fileUploadLoading: {},
            fileInputKeys: {}, // 파일 input 강제 리렌더를 위한 key
            isOAuthLoading: false,
            selectedTeamspaces: [],
        };
    },
    computed: {
        ...mapGetters('user', [
            'accountId',
            'isKoUser',
            'isJPUser',
            'accountToken',
            'isAdmin',
            'hasOnlyAvailableConnectors',
            'role',
        ]),
        ...mapState('asset', { assets: 'connections' }),
        ...mapGetters('view', ['subscriptionWarningVisible']),
        checkIsValid() {
            return R.all(
                R.ifElse(
                    (form) => {
                        const { required } = form;
                        const isDisplayed = this.checkFormShouldDisplay(form);

                        return isDisplayed && required;
                    },
                    checkValidValue,
                    R.T
                ),
                this.formData
            );
        },
        formResetMap() {
            return this.formData.reduce((mainAcc, mainVal) => {
                const { dynamicChoices: { args = [] } = {}, name } = mainVal;

                if (isValueExist(args)) {
                    const mapped = args.reduce((argsAcc, argsVal) => {
                        argsAcc[argsVal] = isValueExist(mainAcc[argsVal])
                            ? R.uniq([...mainAcc[argsVal], name])
                            : [name];
                        return argsAcc;
                    }, {});

                    mainAcc = { ...mainAcc, ...mapped };
                }

                return mainAcc;
            }, {});
        },
        isUnblockPage() {
            return this.$route.name === 'DataTransfer';
        },
    },
    methods: {
        ...mapMutations('view', ['setNotiMsgs']),
        ...mapActions('asset', ['fetchAssets']),
        ...mapActions('iframe', ['postIframeMessage']),
        focusSearchInput() {
            setTimeout(() => {
                const searchEl = document.getElementById(
                    'dashboard-connection-search'
                );
                if (searchEl) {
                    searchEl.focus();
                }
            }, 0);
        },
        handleOAuthCancel() {
            if (this.removeEventFn) {
                this.removeEventFn();
            }
            this.offLoad();
            this.isOAuthLoading = false;
        },
        renderFailNoti({
            code = '',
            message = '',
            locale = '',
            customFallback = '',
            traceId = '',
        } = {}) {
            const errorDetail = code && message
                ? `${code}: ${message}`
                : code || message || '';

            const titleText =
                locale ||
                customFallback ||
                this.$t('CONNECTION.common_connect_fail');

            const copiedText = this.$t('COMMON.copied');

            const title = traceId
                ? `${titleText}<br/><span style="font-size:12px;opacity:0.8;">Trace ID: <span style="cursor:pointer;text-decoration:underline;" onclick="var id='${traceId}',msg='${copiedText}';navigator.clipboard.writeText(id);this.textContent=msg;this.style.textDecoration='none';var el=this;setTimeout(function(){el.textContent=id;el.style.textDecoration='underline'},2000)">${traceId}</span></span>`
                : titleText;

            return this.setNotiMsgs([
                {
                    type: 'FAIL',
                    title,
                    disableTimer: true,
                    toggleInfo: {
                        triggerText: this.$t('COMMON.error_detail'),
                        message: errorDetail,
                    },
                },
            ]);
        },
        offLoad() {
            this.loadingMask = false;
        },
        onClose() {
            this.$emit('close');
        },
        showHubspotMsg() {
            this.loadChatbot(true);
        },
        fetchConnectionTypes() {
            const {
                typePage: page,
                searchTerm,
                accountId: workspaceId,
                connectionTypesIds,
                focusSearchInput,
                isFromAdgen,
            } = this;
            return AssetService.getConnectionTypes({
                workspaceId,
                searchTerm,
                page,
                filtersIds: connectionTypesIds,
                isFromAdgen,
            })
                .then((data) => {
                    if (page > 0) {
                        this.connectionTypes = R.uniq([
                            ...this.connectionTypes,
                            ...data,
                        ]);
                    } else {
                        this.connectionTypes = data;
                    }

                    return data;
                })
                .finally(() => {
                    this.loading = false;
                    focusSearchInput();
                });
        },
        fetchConnections(connectionTypesIds) {
            this.loadingMask = true;
            return AssetService.getConnections({
                workspaceId: this.accountId,
                connectionTypesIds,
                token: this.token,
            })
                .then((data) => {
                    data = R.filter(R.prop('isAuthorized'), data);
                    if (isValueExist(data)) {
                        this.connections = data;
                    }
                    return data;
                })
                .finally(this.offLoad);
        },
        async onClickDatastreamType({
            connectionTypesIds = [],
            forceNewConnection,
        }) {
            if (this.accountId && !forceNewConnection) {
                try {
                    await this.fetchConnections(connectionTypesIds);
                } catch (error) {
                    console.error(error);
                    this.page = CONNECTION_TYPES;
                    return;
                }
            }

            if (isValueExist(this.connections)) {
                this.page = CONNECTIONS;
                return;
            } else if (
                !isValueExist(this.connections) &&
                R.equals(this.role, VIEW_AND_EDIT_ROLE.value)
            ) {
                this.dialog({
                    text: this.$t(
                        'CONNECTION.click_connection_item_permission_error'
                    ),
                    hasCancelButton: false,
                });

                this.onClose();
                return;
            }

            const typeIdsLength = connectionTypesIds.length;

            switch (typeIdsLength) {
                case 0: {
                    const {
                        options = [],
                        helpUrl,
                        fieldToSplitDataSourceCreation,
                    } = await AssetService.getDatastreamOptions({
                        datastreamTypeId: this.datastreamTypeId,
                        token: this.token,
                        connectionId: this.connectionId,
                    });
                    this.prevPage = this.isConnectionPage
                        ? undefined
                        : DATASTREAM_TYPES;
                    this.helpUrl = helpUrl;
                    this.fieldToSplitDataSourceCreation =
                        fieldToSplitDataSourceCreation;
                    this.setStreamForm(options);
                    break;
                }
                case 1: {
                    this.onClickNewConneciton(R.head(connectionTypesIds));
                    break;
                }
                default: {
                    this.page = CONNECTION_TYPES;
                }
            }
        },
        renderConnectionType(item) {
            const {
                name,
                id,
                logo,
                connectionTypesIds,
                type,
                haveAccess,
                source,
                displayChannelNameKR,
            } = item;

            const isLogoSrc = R.is(String, logo);

            return (
                <S.ItemWrapper
                    key={id}
                    onClick={() => {
                        if (this.page === CONNECTION_TYPES) {
                            this.prevPage = this.isConnectionPage
                                ? undefined
                                : CONNECTION_TYPES;
                            this.onClickNewConneciton(id);
                        } else {
                            if (type) {
                                this.handleActionBtnClick(type);
                            } else {
                                if (isValueExist(this.pricePlanSettings)) {
                                    const {
                                        nbOfConnections = Number.MAX_SAFE_INTEGER,
                                    } = this.pricePlanSettings;

                                    const assetLength = R.compose(
                                        R.length,
                                        filterDeprecated
                                    )(this.assets);

                                    if (
                                        nbOfConnections &&
                                        nbOfConnections <= assetLength
                                    ) {
                                        this.renderWarningToLimiedUser();
                                        return;
                                    }
                                }

                                this.datastreamTypeId = id;
                                this.connectionTypesIds = connectionTypesIds;
                                this.haveAccess = haveAccess;
                                this.prevPage = DATASTREAM_TYPES;
                                this.onClickDatastreamType({
                                    connectionTypesIds,
                                });
                            }
                        }
                    }}
                >
                    <S.ItemLeftWrapper>
                        {logo ? (
                            isLogoSrc ? (
                                <S.ConnectionLogo src={logo}></S.ConnectionLogo>
                            ) : (
                                <logo />
                            )
                        ) : (
                            this.channelIconWithAdverity(id || type)
                        )}
                        <S.ItemSpan>
                            {this.language === 'ko'
                                ? displayChannelNameKR || name
                                : name}
                        </S.ItemSpan>
                    </S.ItemLeftWrapper>

                    {!type && source === 'adverity' && (
                        <CrownIcon v-view-box class="crown-icon"></CrownIcon>
                    )}
                </S.ItemWrapper>
            );
        },
        handleScrollObserver: debounce(function (entries = []) {
            const { intersectionRatio } = entries[0];
            const {
                page,
                connectionTypes,
                datastreamTypes,
                fetchConnectionTypes,
                fetchDatastreamTypes,
            } = this;

            const isConnectionType = page === CONNECTION_TYPES;

            const types = isConnectionType ? connectionTypes : datastreamTypes;
            const fetchAPI = isConnectionType
                ? fetchConnectionTypes
                : fetchDatastreamTypes;

            if (intersectionRatio > 0 && isValueExist(types)) {
                this.loading = true;
                this.typePage += 1;
                fetchAPI();
            }
        }, 200),
        renderConnectionTypes() {
            const {
                loading,
                connectionTypes,
                renderConnectionType,
                handleScrollObserver,
            } = this;

            return (
                <S.ComponentWrapper>
                    {!isValueExist(connectionTypes) ? (
                        <S.LoaderWrapper>
                            {loading ? (
                                <RingLoader />
                            ) : (
                                <div>
                                    {this.$t(
                                        'CONNECTION.dashboard_no_list_connection_type'
                                    )}
                                </div>
                            )}
                        </S.LoaderWrapper>
                    ) : (
                        <S.ListWrapper>
                            {connectionTypes.map(renderConnectionType)}
                            {isValueExist(connectionTypes) && (
                                <Intersect onEnter={handleScrollObserver}>
                                    <S.LoaderWrapper>
                                        {loading && <RingLoader />}
                                    </S.LoaderWrapper>
                                </Intersect>
                            )}
                        </S.ListWrapper>
                    )}
                </S.ComponentWrapper>
            );
        },
        observeSearchTerm$() {
            const { generateWatchNew$: gen$, fetchBySearchTerm } = this;

            const page$ = gen$('page');

            const searchTerm$ = page$.pipe(
                filter((v) => [CONNECTION_TYPES, DATASTREAM_TYPES].includes(v)),
                tap(() => (this.loading = true)),
                switchMap(() => gen$('searchTerm').pipe(debounceTime(500)))
            );

            this.$subscribeTo(searchTerm$, fetchBySearchTerm);
        },
        fetchBySearchTerm() {
            this.typePage = 0;

            if (this.page === CONNECTION_TYPES) {
                this.fetchConnectionTypes();
            } else if (this.page === DATASTREAM_TYPES) {
                this.fetchDatastreamTypes();
            }
        },
        renderConnections() {
            const { loading, connections, renderEachConnection } = this;
            return (
                <S.ComponentWrapper>
                    {!isValueExist(connections) ? (
                        <S.LoaderWrapper>
                            {loading ? (
                                <RingLoader />
                            ) : (
                                <div>
                                    {this.$t(
                                        'CONNECTION.dashboard_no_list_connections'
                                    )}
                                </div>
                            )}
                        </S.LoaderWrapper>
                    ) : (
                        <S.ListWrapper>
                            <S.ListWrapper marginBottom={36}>
                                {connections.map(renderEachConnection)}
                            </S.ListWrapper>
                        </S.ListWrapper>
                    )}
                    {!R.equals(this.role, VIEW_AND_EDIT_ROLE.value) && (
                        <S.NewConnectionButton
                            onClick={() => {
                                this.prevPage = CONNECTIONS;
                                this.page = CONNECTION_TYPES;
                            }}
                        >
                            <S.NewConnectionText>
                                {this.$t(
                                    'CONNECTION.dashboard_btn_new_connection'
                                )}
                            </S.NewConnectionText>
                        </S.NewConnectionButton>
                    )}
                </S.ComponentWrapper>
            );
        },
        renderEachConnection({
            id,
            connectionTypeOptions,
            isAuthorized,
            createdAt,
            meta,
            connectionName,
        }) {
            const {
                ConnectionTypeName,
                ConnectionTypeNameKR,
                connectionTypeId,
                logo,
            } = connectionTypeOptions || {};

            return (
                <S.SpaceBetweenItemWrapper
                    isAuthorized={isAuthorized}
                    key={id}
                    onClick={() => {
                        if (isAuthorized) {
                            if (this.haveAccess) {
                                AssetService.getDatastreamOptions({
                                    datastreamTypeId: this.datastreamTypeId,
                                    token: this.token,
                                    connectionId: id,
                                }).then(
                                    ({
                                        options = [],
                                        helpUrl,
                                        fieldToSplitDataSourceCreation,
                                    }) => {
                                        this.connectionId = id;
                                        this.prevPage = CONNECTIONS;
                                        this.helpUrl = helpUrl;
                                        this.fieldToSplitDataSourceCreation =
                                            fieldToSplitDataSourceCreation;
                                        this.setStreamForm(options);
                                    }
                                );
                            } else {
                                this.renderNoAccessDialog({ isClosing: false });
                            }
                        } else {
                            this.prevPage = CONNECTIONS;
                            this.onClickNewConneciton(connectionTypeId);
                        }
                    }}
                >
                    <S.ItemLeftWrapper>
                        <S.ConnectionLogo src={logo}></S.ConnectionLogo>
                        <S.ConnectionNameWrapper>
                            {meta?.connectionName && (
                                <S.ItemSpan>{`${this.$t(
                                    'CONNECTION.connection_name_title'
                                )}: ${meta?.connectionName}`}</S.ItemSpan>
                            )}
                            <S.ItemSpan>{`${this.$t(
                                'COMMON.created_at'
                            )}: ${commDateFormat(
                                createdAt,
                                this.$t('COMMON.date_time_format'),
                                this.language
                            )}`}</S.ItemSpan>
                            {(meta?.userInfo?.name ||
                                meta?.userInfo?.email) && (
                                <S.ItemSpan>
                                    {`${this.$t('COMMON.created_by')}: ${
                                        meta?.userInfo?.name ??
                                        meta?.userInfo?.email
                                    }`}
                                </S.ItemSpan>
                            )}
                            <S.ItemSpan>
                                {`${this.$t('CONNECTION.channel')}: `}
                                {this.language === 'ko'
                                    ? ConnectionTypeNameKR || ConnectionTypeName
                                    : ConnectionTypeName}
                            </S.ItemSpan>
                            {meta?.type && (
                                <S.ItemSpan>
                                    {`${this.$t('COMMON.type')}: ${meta?.type}`}
                                </S.ItemSpan>
                            )}
                        </S.ConnectionNameWrapper>
                    </S.ItemLeftWrapper>
                    <S.ItemRightWrapper>
                        <TrashIcon
                            v-view-box
                            onClick={(e) => {
                                e.stopPropagation();
                                this.dialog({
                                    hasCancelButton: true,
                                    child: (
                                        <div>
                                            <S.DialogText>
                                                {this.$t(
                                                    'CONNECTION.dashboard_message_delete_connection'
                                                )}
                                            </S.DialogText>
                                        </div>
                                    ),
                                    confirmCb: () => {
                                        AssetService.deleteConnection(id)
                                            .then(() => {
                                                this.connections = R.reject(
                                                    R.propEq('id', id),
                                                    this.connections
                                                );
                                            })
                                            .catch((e) => {
                                                const code = R.pathOr(
                                                    '',
                                                    [
                                                        'response',
                                                        'data',
                                                        'code',
                                                    ],
                                                    e
                                                );
                                                if (
                                                    code ===
                                                    'CONNECTION_USED_BY_ASSETS_DELETION_IMPOSSIBLE'
                                                ) {
                                                    this.renderFailNoti({
                                                        locale: this.$t(
                                                            'CONNECTION.dashboard_error_message_delete_connection'
                                                        ),
                                                    });
                                                }
                                            });
                                    },
                                });
                            }}
                        ></TrashIcon>
                    </S.ItemRightWrapper>
                </S.SpaceBetweenItemWrapper>
            );
        },
        onClickNewConneciton(connectionTypeId) {
            this.loadingMask = true;
            return AssetService.getConnectionOptions({
                id: connectionTypeId,
                token: this.token,
            })
                .then((value) => {
                    const { type, options } = value;
                    this.connectionType = type;
                    if (
                        type === 'form' ||
                        (type === 'auth' && isValueExist(options))
                    ) {
                        this.handleformNewConnection(value);
                    } else {
                        this.handleAuthNewConnection(value);
                    }
                })
                .catch((error) => {
                    this.loadingMask = false;
                    const errorMessage = this.handleErrorMessage(error);
                    this.renderFailNoti(errorMessage);
                    if (this.prevPage) {
                        this.page = this.prevPage;
                    }
                });
        },
        onRefreshConnection({ connectionId, connectionTypeId }) {
            this.loadingMask = true;

            return AssetService.getConnectionOptions({
                id: connectionTypeId,
                token: this.token,
            })
                .then((value) => {
                    const { type, options } = value;
                    this.connectionType = type;
                    const params = { ...value, connectionId, isRefresh: true };

                    if (
                        type === 'form' ||
                        (type === 'auth' && isValueExist(options))
                    ) {
                        this.handleFormRefreshConnction(params);
                    } else {
                        this.handleAuthRefreshConnection(params);
                    }
                })
                .catch((error) => {
                    this.loadingMask = false;
                    const errorMessage = this.handleErrorMessage(error);
                    this.renderFailNoti(errorMessage);
                    if (this.prevPage) {
                        this.page = this.prevPage;
                    }
                });
        },
        async handleAuthRefreshConnection({ connectionTypeId, connectionId }) {
            const {
                token,
                renderGoogleInfoModal,
                handleAuthLink,
                renderFailNoti,
                onClose,
                offLoad,
            } = this;

            try {
                const { url } = await AssetService.getOAuthRedirectUrl({
                    id: connectionId,
                    token,
                });

                if (!url) {
                    offLoad();
                    renderFailNoti();
                    return;
                }

                if (GOOGLE_CONNECTORS.has(connectionTypeId)) {
                    renderGoogleInfoModal({
                        type: connectionTypeId,
                        cb: () =>
                            handleAuthLink({
                                url,
                                isRefresh: true,
                                connectionId,
                                customCancelCb: onClose,
                                connectionTypeId,
                                skipLoadOffWatch:
                                isOauthCOOPConnectors(connectionTypeId),
                            }),
                        cancelCb: onClose,
                    });
                    return;
                }

                handleAuthLink({
                    url,
                    isRefresh: true,
                    connectionId,
                    customCancelCb: onClose,
                    connectionTypeId,
                    skipLoadOffWatch:
                    isOauthCOOPConnectors(connectionTypeId),
                });
            } catch (error) {
                offLoad();
                const errorMessage = this.handleErrorMessage(error);
                renderFailNoti(errorMessage);
            }
        },
        handleFormRefreshConnction(value) {
            const { connectionId } = value;

            if (connectionId) {
                this.connectionId = connectionId;
            }

            this.handleformNewConnection(value);
        },
        async handleAuthConnection({
            connectionTypeId,
            options,
            connectionId,
        }) {
            const {
                renderFailNoti,
                accountId: workspaceId,
                token,
                renderGoogleInfoModal,
                handleAuthLink,
            } = this;

            let params = { id: connectionId, token };
            if (isValueExist(options)) {
                params = { ...params, workspaceId, options };
            }

            let { isAuthorized, isOauth, url } =
                await this.handleCheckingAuthConnection(params);

            if ((isOauth && !url) || (!isOauth && !isAuthorized)) {
                this.loadingMask = false;
                renderFailNoti({
                    customFallback: this.$t('CONNECTION.oauth_fail_noti'),
                });
                return;
            }

            if (GOOGLE_CONNECTORS.has(connectionTypeId)) {
                renderGoogleInfoModal({
                    type: connectionTypeId,
                    cb: () =>
                        handleAuthLink({
                            url,
                            isRefresh: false,
                            connectionId,
                            connectionTypeId,
                            skipLoadOffWatch:
                                isOauthCOOPConnectors(connectionTypeId),
                        }),
                    cancelCb: () => (this.page = CONNECTIONS),
                });
                return;
            }

            handleAuthLink({
                url,
                isRefresh: false,
                connectionId,
                connectionTypeId,
                skipLoadOffWatch: isOauthCOOPConnectors(connectionTypeId),
            });
        },
        handleAuthLink({
            url,
            isRefresh,
            connectionId,
            connectionTypeId,
            customCancelCb = R.identity,
            refetchInterval = 5000,
            skipLoadOffWatch = false,
        }) {
            const {
                offLoad,
                renderNoAccessDialog,
                setStreamForm,
                fetchConnections,
                haveAccess,
                datastreamTypeId,
                token,
            } = this;

            this.loadingMask = true;
            this.isOAuthLoading = true;
            let connected = false;

            const onSuccess = async (e) => {
                if (e) {
                    const success = R.path(['data', 'success'], e);

                    if (!success) {
                        if (success === false) {
                            console.error('auth failed', e);

                            const error = R.pathOr(
                                { code: '', message: '', traceId: '' },
                                ['data', 'error'],
                                e
                            );
                            const errorInfo = this.handleErrorMessage({
                                response: { data: error },
                            });
                            this.renderFailNoti({
                                ...errorInfo,
                                customFallback: this.$t(
                                    'CONNECTION.oauth_fail_noti'
                                ),
                            });
                            this.removeEventFn();

                            offLoad();

                            if (isRefresh) {
                                this.onClose();
                            }
                        }
                        // success: undefined => guess other event received
                        return;
                    }
                }
                console.log('onSucess from auth link', e);
                this.removeEventFn();
                connected = true;
                this.isOAuthLoading = false;

                if (isRefresh) {
                    offLoad();
                    if (this.$route?.query?.dataSourceToken) {
                        const reconnectedAsset =
                            await AssetService.getAssetWithToken(
                                this.$route.query.dataSourceToken
                            );

                        this.$emit('refresh', reconnectedAsset);
                    } else {
                        this.postIframeMessage({
                            type: IFRAME_EVENT_MESSAGE.FORCE_FETCH_CONNECTED_ASSETS,
                            origin: 'VUE',
                        });
                    }
                    this.onClose();
                    return;
                }

                AssetService.getDatastreamOptions({
                    datastreamTypeId,
                    token,
                    connectionId,
                })
                    .then(
                        ({
                            options = [],
                            helpUrl,
                            fieldToSplitDataSourceCreation,
                        }) => {
                            if (haveAccess) {
                                this.connectionId = connectionId;
                                this.helpUrl = helpUrl;
                                this.fieldToSplitDataSourceCreation =
                                    fieldToSplitDataSourceCreation;
                                setStreamForm(options);
                            } else {
                                renderNoAccessDialog({
                                    isClosing: true,
                                });
                            }
                        }
                    )
                    .finally(offLoad);
            };

            const cancelCb = (e) => {
                console.log(e, 'cancelCb from auth link');
                if (connected) {
                    offLoad();
                    return;
                }

                this.removeEventFn();

                offLoad();
                this.isOAuthLoading = false;
                customCancelCb();
            };

            const cb = (e = {}) => {
                console.log(e, 'cb from auth link');
                const { origin } = e;
                if (!checkOrigin(origin)) {
                    return console.error('not matched origin', origin);
                }

                onSuccess(e);
            };

            this.removeEventFn = showAuthLink({
                url,
                cancelCb,
                cb,
                skipPopupClosedCheck: skipLoadOffWatch,
            });

            // For COOP connectors, watch isOAuthLoading (controlled by Cancel button)
            // For others, watch loadingMask (controlled by popup.closed detection)
            const watchProperty = skipLoadOffWatch
                ? 'isOAuthLoading'
                : 'loadingMask';
            const [__, loadOff$] = this.generateWatchNew$(watchProperty).pipe(
                partition(Boolean)
            );

            const connected$ = interval(Number(refetchInterval)).pipe(
                switchMap(() =>
                    from(
                        (() => {
                            console.log('getConnections from auth link');
                            return AssetService.getConnections({
                                workspaceId: this.accountId,
                                connectionTypesIds: [connectionTypeId],
                                token: this.token,
                            }).then((data) => {
                                return R.filter(R.prop('isAuthorized'), data);
                            });
                        })()
                    ).pipe(
                        filter(isValueExist),
                        map((data) =>
                            data.some(({ id }) => id === connectionId)
                        ),
                        catchError(() => of(false))
                    )
                ),

                filter(R.identity),
                take(1),
                takeUntil(loadOff$)
            );
            this.$subscribeTo(connected$, () => {
                console.log('connected$ from auth link', connected$);
                onSuccess();
            });
        },
        scrollFormToTop() {
            this.$nextTick(() => {
                const editArea = document.querySelector('.dashboard-drawer-edit-area');
                if (editArea) {
                    editArea.scrollTop = 0;
                }
            });
        },
        handleformNewConnection({ connectionTypeId, options, helpUrl }) {
            this.connectionTypeId = connectionTypeId;
            this.helpUrl = helpUrl;
            this.formData = refinedInitialFormData(options);
            this.page = FORM_CONNECTION;
            this.prevPage = CONNECTIONS;
            this.loadingMask = false;
            this.scrollFormToTop();
        },
        async handleAuthNewConnection({ connectionTypeId, options }) {
            const { makeConnection, handleAuthConnection } = this;

            this.loadingMask = true;
            const { id: connectionId } = await makeConnection(connectionTypeId);

            return handleAuthConnection({
                connectionId,
                connectionTypeId,
                options,
            });
        },
        makeConnection(connectionTypeId) {
            return AssetService.addConnections({
                workspaceId: this.accountId,
                connectionTypeId,
                token: this.token,
            })
                .then(R.identity)
                .catch((e) => {
                    const errorMessage = this.handleErrorMessage(e);
                    this.renderFailNoti(errorMessage);
                })
                .finally(this.offLoad);
        },
        setStreamForm(options) {
            this.formData = refinedInitialFormData(options);
            this.page = FORM_DATASTREAM;
            this.scrollFormToTop();
        },
        renderForms() {
            const filteredFormData = this.formData.filter(
                this.checkFormShouldDisplay
            );

            const [required, optional] = R.partition(
                R.prop('required'),
                filteredFormData
            );

            const hasOnlyRequired = !isValueExist(optional);

            return (
                <S.ComponentWrapper>
                    <S.FormListWrapper>
                        {required.map(this.renderEachForm)}
                    </S.FormListWrapper>
                    {!hasOnlyRequired && (
                        <S.FormOptionWrapper>
                            <S.FormOptionTitleWrapper
                                opened={this.formOptionOpened}
                                onClick={() =>
                                    (this.formOptionOpened =
                                        !this.formOptionOpened)
                                }
                            >
                                <span>
                                    {this.$t(
                                        'CONNECTION.dashboard_option_title'
                                    )}
                                </span>
                                <Accordion v-view-box class="accordion" />
                            </S.FormOptionTitleWrapper>
                            {this.formOptionOpened && (
                                <S.FormOptionContetnWrapper>
                                    {optional.map(this.renderEachForm)}
                                </S.FormOptionContetnWrapper>
                            )}
                        </S.FormOptionWrapper>
                    )}
                    {this.page === FORM_DATASTREAM && (
                        <TeamspaceAccess
                            value={this.selectedTeamspaces}
                            onInput={(v) => (this.selectedTeamspaces = v)}
                        />
                    )}
                    {this.isPremium && this.page === FORM_CONNECTION && (
                        <S.HelpText>
                            {this.$t(
                                'CONNECTION.bot_connection_request_description'
                            )}
                        </S.HelpText>
                    )}
                </S.ComponentWrapper>
            );
        },
        checkFormShouldDisplay(item) {
            const { displayConditions } = item;

            if (!displayConditions) return true;

            const formDataByName = R.indexBy(R.prop('name'), this.formData);

            return R.all((condition) => {
                const { creationOptionName, possibleValues = [] } = condition;
                const condValue =
                    formDataByName[creationOptionName]?.value ?? '';

                return possibleValues.includes(condValue);
            }, displayConditions);
        },
        renderEachForm(item) {
            const {
                type,
                helpText = '',
                helpTextKR = '',
                helpTextJP = '',
                label = '',
                labelKR = '',
                labelJP = '',
                name,
                value,
                required,
                id,
                placeholder = '',
                placeholderKR = '',
                fileType = [],
            } = item;

            const index = R.findIndex(R.propEq('id', id), this.formData);

            let helpTextLocaled = this.isKoUser && helpTextKR
                ? helpTextKR
                : this.isJPUser && helpTextJP
                ? helpTextJP
                : helpText;

            if (helpTextLocaled.includes('<a')) {
                helpTextLocaled = R.replace(
                    /<a/g,
                    "<a target='_blank'",
                    helpTextLocaled
                );
            }

            if (
                this.fieldToSplitDataSourceCreation &&
                this.fieldToSplitDataSourceCreation === name
            ) {
                helpTextLocaled += `${helpTextLocaled ? '\n\n' : ''}*${this.$t(
                    'CONNECTION.multi_select_field_to_split_warning'
                )}`;
            }

            const labelLocaled = this.isKoUser && labelKR
                ? labelKR
                : this.isJPUser && labelJP
                ? labelJP
                : label;

            const placeholderLocaled = this.isKoUser
                ? placeholderKR || placeholder
                : placeholder;

            const onInput = (v) =>
                (this.formData = R.assocPath(
                    [index, 'value'],
                    v,
                    this.formData
                ));

            return (
                <EditBox title={upperHead(labelLocaled)} isFocus={true}>
                    <S.InputWrapper>
                        {type === 'choice' || type === 'multi_choice' ? (
                            this.renderDropdown(item, index)
                        ) : type === 'boolean' ? (
                            <Checkbox
                                value={value || false}
                                onInput={onInput}
                                hasBorder={true}
                            />
                        ) : type === 'date' ? (
                            [
                                <DatePicker
                                    value={value}
                                    onUpdate={onInput}
                                    placeholder={placeholderLocaled}
                                />,
                                <S.ErrorText>
                                    {required && !value
                                        ? this.$t('COMMON.field_required')
                                        : ''}
                                </S.ErrorText>,
                            ]
                        ) : type === 'file' ? (
                            [
                                <div>
                                    <input
                                        key={this.fileInputKeys[name] || 0}
                                        type="file"
                                        accept={fileType.join(',')}
                                        multiple={false}
                                        onClick={() => {
                                            // 업로드된 파일이 있고 새로 선택하려는 경우에만 key 변경
                                            const currentValue =
                                                this.formData.find(
                                                    R.propEq('name', name)
                                                )?.value;
                                            if (currentValue) {
                                                this.fileInputKeys = {
                                                    ...this.fileInputKeys,
                                                    [name]:
                                                        (this.fileInputKeys[
                                                            name
                                                        ] || 0) + 1,
                                                };
                                            }
                                        }}
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                this.handleFileUpload(
                                                    file,
                                                    name,
                                                    fileType
                                                );
                                            }
                                        }}
                                        disabled={this.fileUploadLoading[name]}
                                        style={{ width: '100%' }}
                                    />
                                    {this.fileUploadLoading[name] && (
                                        <div style={{ marginTop: '8px' }}>
                                            <RingLoader size={16} />
                                        </div>
                                    )}
                                    {fileType.length > 0 && (
                                        <S.FileTypeHint>
                                            {this.$t(
                                                'COMMON.allowed_file_type',
                                                { types: fileType.join(', ') }
                                            )}
                                        </S.FileTypeHint>
                                    )}
                                </div>,
                                this.formData.find(R.propEq('name', name))
                                    ?.value && (
                                    <S.UploadedFileWrapper>
                                        <S.UploadedFileUrl
                                            onClick={() => {
                                                window.open(
                                                    prependPublicMediaUrl(
                                                        this.formData.find(
                                                            R.propEq(
                                                                'name',
                                                                name
                                                            )
                                                        )?.value
                                                    ),
                                                    '_blank'
                                                );
                                            }}
                                        >
                                            {prependPublicMediaUrl(
                                                this.formData.find(
                                                    R.propEq('name', name)
                                                )?.value
                                            )}
                                        </S.UploadedFileUrl>
                                        <XIcon
                                            v-view-box
                                            onClick={() =>
                                                this.removeUploadedFile(name)
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                marginLeft: '8px',
                                                width: '16px',
                                                height: '16px',
                                            }}
                                        />
                                    </S.UploadedFileWrapper>
                                ),
                                <S.ErrorText>
                                    {required && !value
                                        ? this.$t('COMMON.field_required')
                                        : ''}
                                </S.ErrorText>,
                            ]
                        ) : (
                            <InputField
                                type={
                                    name.includes('password')
                                        ? 'password'
                                        : type === 'string'
                                        ? 'text'
                                        : type
                                }
                                value={value || ''}
                                onInput={onInput}
                                errorText={
                                    required && !value
                                        ? this.$t('COMMON.field_required')
                                        : ''
                                }
                                errorDirection="right"
                                autocomplete={
                                    name.includes('password')
                                        ? 'new-password'
                                        : 'nope'
                                }
                                id={name}
                                cleaner={
                                    type === 'integer'
                                        ? R.replace(/[^0-9]/g, '')
                                        : R.identity
                                }
                                {...(placeholderLocaled && {
                                    placeholderLocaled,
                                })}
                            />
                        )}
                        {this.isCustomFieldUser &&
                            this.datastreamTypeId === 'internal_bigQuery' &&
                            name === 'customFilters' && (
                                <S.CustomFieldPanelTrigger
                                    onClick={() => {
                                        this.customFieldPanelProps = {
                                            value,
                                            onInput,
                                        };
                                        this.customFieldPanelOpend = true;
                                    }}
                                >
                                    {this.$t(
                                        'EXPORT_DATA.custom_field_open_editor'
                                    )}
                                </S.CustomFieldPanelTrigger>
                            )}

                        <S.HelpText
                            domPropsInnerHTML={upperHead(helpTextLocaled)}
                        ></S.HelpText>
                    </S.InputWrapper>
                </EditBox>
            );
        },
        renderDropdown(item, index) {
            const {
                type,
                choices = [],
                value,
                dropdown,
                dynamicChoices,
                required,
                name,
            } = item;

            const isMultiChoice = type === 'multi_choice';
            const { setSearchTerm } = this;

            const updateForm = (key, pred) => {
                this.formData = R.over(
                    R.lensPath([index, key]),
                    pred,
                    this.formData
                );
            };

            const clickDropdown = () => {
                setSearchTerm('');

                this.formData = R.map(
                    R.when(
                        R.allPass([
                            R.has('dropdown'),
                            R.complement(R.propEq('name', name)),
                        ]),
                        R.assoc('dropdown', false)
                    ),
                    this.formData
                );

                updateForm('dropdown', R.not);
            };

            const updateValue = (v) => updateForm('value', R.always(v));

            const findDisplayValue = (value) => {
                return R.compose(
                    this.getChoiceDisplayName,
                    R.find(R.propEq('value', value))
                )(choices);
            };

            const hasDynamicChoices = isValueExist(dynamicChoices);

            const width = (() => {
                if (dropdown) {
                    const el$ = document.getElementById(
                        `adverity-dropdown-${index}`
                    );

                    if (el$) {
                        const { width = 320 } = el$.getBoundingClientRect();
                        return width;
                    }
                }

                return 320;
            })();

            const boxProps = {
                attrs: {
                    selectedOption: value,
                    tooltipGenerator: findDisplayValue,
                    translator: (value) => {
                        if (R.isNil(value)) {
                            value = isMultiChoice ? [] : '';
                        }

                        return isMultiChoice
                            ? (() => {
                                  let name = R.compose(
                                      findDisplayValue,
                                      R.path([0, 'value'])
                                  )(value);

                                  const selectedNum = value.length - 1;

                                  if (selectedNum > 0) {
                                      name += ` (+ ${selectedNum})`;
                                  }

                                  return name;
                              })()
                            : findDisplayValue(value);
                    },
                    isMultiple: isMultiChoice,
                    id: `adverity-dropdown-${index}`,
                    ...(hasDynamicChoices &&
                        (() => {
                            let additionals = {};
                            const fields =
                                this.makeRequiredFieldList(dynamicChoices);

                            if (isValueExist(fields)) {
                                additionals = {
                                    disabled: true,
                                    tooltipGenerator: R.always(
                                        this.$t(
                                            'CONNECTION.dashboard_option_multiple_required',
                                            {
                                                fields: fields
                                                    .map(upperHead)
                                                    .join(', '),
                                            }
                                        )
                                    ),
                                };
                            }

                            return additionals;
                        })()),
                },
                on: {
                    click: () => {
                        if (hasDynamicChoices && !dropdown) {
                            this.handleDynamicChoice({
                                updateForm,
                                clickDropdown,
                                item,
                            });
                        } else {
                            clickDropdown();
                        }
                    },
                },
            };

            const dropdownProps = {
                attrs: {
                    isVisible: dropdown,
                    value,
                    optionList: choices,
                    translator: this.getChoiceDisplayName,
                    showSelected: true,
                    width,
                    hasSearchBar: !this.isQuickConnect,
                    tooltipGenerator: this.getChoiceDisplayName,
                    tooltipOnEllipsis: true,
                    useLooseSearch: true,
                    appendToBody: this.isQuickConnect,
                    addtionalToBodyPosition: {
                        top: 35,
                    },
                    allowEmptyValue: true,
                    useVirtualScroll: true,
                },
                on: {
                    confirm: (v) => {
                        const nextValue = isMultiChoice ? v : v.value;

                        // Only cascade-reset dependent fields when the value
                        // actually changes — re-selecting the same option must
                        // not wipe prefilled (or already-chosen) child fields.
                        // `value` here is the closure's current form value.
                        const valueChanged = isMultiChoice
                            ? !R.equals(value, nextValue)
                            : value !== nextValue;

                        updateValue(nextValue);

                        const resetForm = this.formResetMap[name];

                        if (valueChanged && isValueExist(resetForm)) {
                            resetForm.forEach((resetName) => {
                                const resetIdx = R.findIndex(
                                    R.propEq('name', resetName),
                                    this.formData
                                );

                                this.formData = R.assocPath(
                                    [resetIdx, 'value'],
                                    '',
                                    this.formData
                                );
                            });
                        }

                        if (isMultiChoice) return;

                        clickDropdown();
                    },
                    clickOutside: () => {
                        clickDropdown();
                    },
                },
            };

            return (
                <div style="width:100%;">
                    <SelectedBox {...boxProps} />
                    {isMultiChoice ? (
                        <CheckDropdown
                            {...dropdownProps}
                            selectedOption={value || []}
                            hasConfirmButton={false}
                            hasSelectAll={!this.hasOnlyAvailableConnectors}
                            limitedOptions={
                                this.page === FORM_DATASTREAM &&
                                this.fieldToSplitDataSourceCreation &&
                                this.fieldToSplitDataSourceCreation === name
                                    ? {
                                          cnt: (() => {
                                              const nb = R.path(
                                                  [
                                                      'pricePlanSettings',
                                                      'nbOfConnections',
                                                  ],
                                                  this
                                              );

                                              if (!isValueExist(nb)) return;

                                              const assetLength = R.compose(
                                                  R.length,
                                                  filterDeprecated
                                              )(this.assets);

                                              return nb - assetLength;
                                          })(),
                                          callback: () => {
                                              this.renderWarningToLimiedUser();
                                              return;
                                          },
                                      }
                                    : {
                                          cnt: undefined,
                                          callback: () => {},
                                      }
                            }
                        />
                    ) : (
                        <Dropdown
                            {...dropdownProps}
                            selectedOption={
                                isValueExist(value)
                                    ? choices.find(R.propEq('value', value))
                                    : undefined
                            }
                        />
                    )}
                    {required && !isValueExist(value) && (
                        <S.ErrorText>
                            {this.$t('COMMON.field_required')}
                        </S.ErrorText>
                    )}
                </div>
            );
        },
        handleDynamicChoice({ updateForm, clickDropdown, item = {} }) {
            const { dynamicChoices } = item;
            const { args, lookupUrl } = dynamicChoices;

            const inputData = this.refineFormToParam();

            // if (
            //     !isValueExist(args) ||
            //     R.all(R.propSatisfies(isValueExist, R.__, inputData), args)
            // ) {
            this.loadingMask = true;
            return AssetService.getDynamicChoices({
                inputData,
                lookupUrl,
                connectionId: this.connectionId,
                connectionTypeId: this.connectionTypeId,
                token: this.token,
            })
                .then((data) => {
                    const choices = data.map((item) =>
                        item.isAlreadyConnected
                            ? { ...item, disabled: true }
                            : item
                    );
                    updateForm('choices', R.always(choices));

                    const defaultSelectedValue =
                        item.type === 'multi_choice'
                            ? data.filter(R.prop('isSelected'))
                            : data.find(R.prop('isSelected'))?.value;

                    if (
                        isValueExist(defaultSelectedValue) &&
                        !isValueExist(item.value)
                    ) {
                        updateForm('value', R.always(defaultSelectedValue));
                    } else if (
                        item.type === 'multi_choice' &&
                        isValueExist(item.value)
                    ) {
                        // Re-point a prefilled (defaultValue-seeded) value at
                        // the freshly fetched choice objects. CheckDropdown
                        // decides "checked" by deep equality, so the lighter
                        // seeded objects would otherwise show as unchecked once
                        // the real choices load.
                        const reconciled = item.value.map(
                            (sel) =>
                                choices.find(R.propEq('value', sel.value)) ??
                                sel
                        );

                        updateForm('value', R.always(reconciled));
                    }

                    clickDropdown();
                })
                .catch((e) => {
                    const errorMessage = this.handleErrorMessage(e);

                    this.renderFailNoti({
                        ...errorMessage,
                        customFallback: this.$t(
                            'CONNECTION.dynamic_choice_fail_noti'
                        ),
                    });
                })
                .finally(this.offLoad);
            // }
        },
        makeRequiredFieldList(dynamicChoices) {
            const { args = [] } = dynamicChoices;

            const inputData = this.refineFormToParam();

            return R.compose(
                R.map(({ label = '', labelKR = '', labelJP = '' }) =>
                    this.isKoUser && labelKR
                        ? labelKR
                        : this.isJPUser && labelJP
                        ? labelJP
                        : label
                ),
                R.filter(
                    R.allPass([
                        R.propSatisfies((name) => args.includes(name), 'name'),
                        ({ name }) => !isValueExist(inputData[name]),
                    ])
                )
            )(this.formData);
        },
        renderPage() {
            switch (this.page) {
                case CONNECTION_TYPES:
                    return this.renderConnectionTypes();
                case CONNECTIONS:
                    return this.renderConnections();
                case FORM_CONNECTION:
                case FORM_DATASTREAM:
                    return this.renderForms();
                case DATASTREAM_TYPES:
                    return this.renderDatastreamTypes();
            }
        },
        onClickPrevBtn() {
            if (this.prevPage) {
                switch (this.page) {
                    case CONNECTIONS: {
                        this.datastreamTypeId = undefined;
                        break;
                    }
                    case CONNECTION_TYPES:
                        this.typePage = 0;
                        this.searchTerm = '';
                        break;
                    case FORM_CONNECTION:
                        this.formData = [];
                        this.connectionId = undefined;
                        this.connectionTypeId = undefined;
                        this.helpUrl = undefined;
                        break;
                    case FORM_DATASTREAM:
                        this.helpUrl = undefined;
                        this.formData = [];
                        this.connectionId = undefined;
                        this.fieldToSplitDataSourceCreation = '';
                        this.selectedTeamspaces = [];
                        break;
                }

                this.page = this.prevPage;
            }
        },
        renderHeader() {
            const showPrevBtn = (() => {
                if (this.isConnectionPage) {
                    return (
                        this.prevPage &&
                        this.page !== DATASTREAM_TYPES &&
                        this.page !== CONNECTIONS
                    );
                } else {
                    return this.page !== DATASTREAM_TYPES;
                }
            })();

            return (
                <S.MainTitle slot="header">
                    <S.LeftWrapper>
                        <S.MainTitleTop>
                            {showPrevBtn && (
                                <PrevIcon
                                    v-view-box
                                    onClick={this.onClickPrevBtn}
                                />
                            )}
                            {this.page &&
                                this.$t(
                                    `CONNECTION.dashboard_title_${R.toLower(
                                        this.page === FORM_CONNECTION &&
                                            this.isPremium
                                            ? 'BOT_CONNECTION'
                                            : this.page
                                    )}`
                                )}
                        </S.MainTitleTop>
                    </S.LeftWrapper>
                </S.MainTitle>
            );
        },
        fetchDatastreamTypes() {
            return AssetService.getDatastreamTypes({
                page: this.typePage,
                searchTerm: this.searchTerm,
                isFromAdgen: this.isFromAdgen,
            })
                .then((data) => {
                    if (this.typePage > 0) {
                        this.datastreamTypes = [
                            ...this.datastreamTypes,
                            ...data,
                        ];
                    } else {
                        this.datastreamTypes = data;
                    }

                    return data;
                })
                .finally(() => {
                    this.loading = false;
                    this.focusSearchInput();
                });
        },
        renderDatastreamTypes() {
            const {
                searchTerm,
                setSearchTerm,
                datastreamTypes,
                loading,
                renderConnectionType,
                handleScrollObserver,
            } = this;
            return (
                <S.ComponentWrapper>
                    <S.SearchWrapper>
                        <SearchInput
                            ref="search-input"
                            value={searchTerm}
                            onInput={setSearchTerm}
                            iconAlign="right"
                            placeholder={this.$t('NEW_DASHBOARD.search')}
                            disabled={loading}
                            identifier="dashboard-connection-search"
                        ></SearchInput>
                    </S.SearchWrapper>
                    {!isValueExist(datastreamTypes) ? (
                        <S.LoaderWrapper>
                            {loading ? (
                                <RingLoader />
                            ) : (
                                <div>
                                    {this.$t(
                                        'CONNECTION.dashboard_no_list_datastream'
                                    )}
                                </div>
                            )}
                        </S.LoaderWrapper>
                    ) : (
                        <S.ListWrapper>
                            {datastreamTypes.map(renderConnectionType)}
                            {isValueExist(datastreamTypes) && (
                                <Intersect onEnter={handleScrollObserver}>
                                    <S.LoaderWrapper>
                                        {loading && <RingLoader />}
                                    </S.LoaderWrapper>
                                </Intersect>
                            )}
                        </S.ListWrapper>
                    )}
                </S.ComponentWrapper>
            );
        },
        refineFormToParam() {
            const filtered = this.formData.filter(this.checkFormShouldDisplay);

            return filtered.reduce(
                (acc, { name, value, type }) => ({
                    ...acc,
                    ...(isValueExist(value) && {
                        [name]:
                            type === 'integer'
                                ? Number(value)
                                : R.is(String, value)
                                ? value.trim()
                                : value,
                    }),
                }),
                {}
            );
        },
        submitForm(options) {
            const {
                accountId: workspaceId,
                connectionId,
                page,
                connectionTypeId,
                datastreamTypeId,
                refreshAdAccounts,
                haveAccess,
                renderNoAccessDialog,
                fetchConnections,
                showHubspotMsg,
                token,
                isFromAdgen,
            } = this;

            let params = { workspaceId, options, token, isFromAdgen },
                api = Promise.resolve(),
                cb = R.identity;

            if (
                page === FORM_CONNECTION &&
                this.connectionType &&
                this.connectionType === 'auth' &&
                !this.connectionId
            ) {
                this.handleAuthNewConnection({
                    connectionTypeId,
                    options,
                });
                return;
            }

            switch (page) {
                case FORM_CONNECTION: {
                    params = R.compose(
                        R.when(
                            R.always(connectionId),
                            R.assoc('id', connectionId)
                        ),
                        R.mergeLeft({ connectionTypeId })
                    )(params);
                    api = AssetService.addConnections;
                    cb = async ({ id }) => {
                        // refresh connection
                        const { isAuthorized } =
                            await this.handleCheckingAuthConnection({
                                ...params,
                                id: this.connectionId || id,
                            });

                        if (this.connectionId) {
                            if (isAuthorized) {
                                if (this.$route?.query?.dataSourceToken) {
                                    const reconnectedAsset =
                                        await AssetService.getAssetWithToken(
                                            this.$route.query.dataSourceToken
                                        );

                                    this.$emit('refresh', reconnectedAsset);
                                } else {
                                    this.postIframeMessage({
                                        type: IFRAME_EVENT_MESSAGE.REFRESH_AD_ACCOUNTS,
                                    });
                                }

                                this.onClose();
                                return;
                            }

                            if (this.connectionType === 'auth') {
                                return this.handleAuthRefreshConnection({
                                    connectionTypeId,
                                    connectionId,
                                });
                            }
                        }

                        if (!isAuthorized) {
                            this.connectionId = undefined;

                            const helpUrl = this.helpUrl[this.userLang];
                            this.renderFailNoti({
                                customFallback: helpUrl
                                    ? this.$t(
                                          'CONNECTION.form_auth_fail_noti',
                                          { link: helpUrl }
                                      )
                                    : this.$t('CONNECTION.oauth_fail_noti'),
                            });
                        } else if (haveAccess) {
                            const {
                                options = [],
                                helpUrl,
                                fieldToSplitDataSourceCreation,
                            } = await AssetService.getDatastreamOptions({
                                datastreamTypeId,
                                token,
                                connectionId: id,
                            });

                            this.connectionId = id;
                            this.prevPage = DATASTREAM_TYPES;
                            this.helpUrl = helpUrl;
                            this.fieldToSplitDataSourceCreation =
                                fieldToSplitDataSourceCreation;
                            this.setStreamForm(options);
                        } else {
                            renderNoAccessDialog({ isClosing: true });
                        }
                    };
                    break;
                }
                case FORM_DATASTREAM: {
                    // The server resolves the workspace from this token, so a
                    // token that is not our own account's puts the datasource
                    // in another workspace while the picker lists ours, and
                    // sending those ids fails the whole creation.
                    const targetsAnotherWorkspace =
                        Boolean(token) && token !== this.accountToken;
                    // The management list types teamspace ids as strings; this
                    // endpoint matches them against a numeric set, so a string
                    // reads as no access.
                    const teamspaceIds = targetsAnotherWorkspace
                        ? []
                        : this.selectedTeamspaces
                              .map(({ id }) => Number(id))
                              .filter(Boolean);

                    params = R.mergeLeft(
                        {
                            datastreamTypeId,
                            connectionId,
                            ...(isValueExist(teamspaceIds) && { teamspaceIds }),
                        },
                        params
                    );
                    api = AssetService.addDatastream;
                    cb = async (data = {}) => {
                        const assetsBeforeUpdate = this.assets;
                        const { assets, warnings } = data;

                        if (!isValueExist(assets) && isValueExist(warnings)) {
                            const warning = warnings[0];
                            const { code = '', message = '' } = warning;

                            const localeExistCode = `WIDGET.error_panel.error.${code}`;

                            this.renderFailNoti({
                                locale: this.$te(localeExistCode)
                                    ? this.$t(localeExistCode)
                                    : '',
                                code,
                                message,
                                customFallback: this.$t(
                                    'CONNECTION.datasource_creation_fail_noti'
                                ),
                            });
                            return;
                        }

                        const successCb = () => {
                            this.$emit('refresh', assets);
                            this.onClose();
                        };

                        const type = R.pathOr('', [0, 'type'], assets);

                        if (type.includes('adverity')) {
                            this.dialog({
                                hasCancelButton: false,
                                child: (
                                    <div class="connection__dialog__no-access">
                                        <S.DialogText
                                            domPropsInnerHTML={this.$t(
                                                'CONNECTION.dashboard_message_make_datastream'
                                            )}
                                        ></S.DialogText>
                                        <a onClick={() => showHubspotMsg()}>
                                            {this.$t('COMMON.contact_adriel')}
                                        </a>
                                    </div>
                                ),
                                confirmCb: successCb,
                            });
                        } else if (
                            (type.includes('csv') &&
                                type !== 'csvFromSftp' &&
                                type !== 'csvWithDate') ||
                            type === 'naverBlogReport'
                        ) {
                            this.$emit('refresh', data);
                            this.generatedAssetId = R.pathOr(
                                '',
                                [0, 'id'],
                                assets
                            );
                            this.generatedAssetType = type;
                            this.fileConnectorPanelOpened = true;
                        } else {
                            this.showAddedToast(
                                this.isPremium
                                    ? this.$t(
                                          'CONNECTION.bot_connection_request_success'
                                      )
                                    : ''
                            );
                            if (this.isReactDashboard) {
                                this.postIframeMessage({
                                    type: IFRAME_EVENT_MESSAGE.REFRESH_AD_ACCOUNTS,
                                });
                            }
                            successCb();
                        }

                        logGtmConnectionComplete(R.head(assets || []));
                        this.requestGtmEvent({
                            newAssets: assets,
                            curAssets: assetsBeforeUpdate,
                        });
                    };
                    break;
                }
            }

            this.loadingMask = true;
            return api(params)
                .then(cb)
                .catch((e) => {
                    if ([FORM_CONNECTION, FORM_DATASTREAM].includes(page)) {
                        // return Promise.all([
                        // AssetService.deleteConnection(this.connectionId),
                        // fetchConnections(),
                        // ]).then(() => {
                        // this.connectionId = undefined;

                        const { code = '' } = R.pathOr(
                            {},
                            ['response', 'data'],
                            e
                        );

                        if (code === 'MAX_NUMBER_OF_DATA_SOURCES_EXCEEDED') {
                            this.renderWarningToLimiedUser();
                            return;
                        }

                        const errorMessage = this.handleErrorMessage(e);

                        if (page === FORM_DATASTREAM) {
                            gtm.logEvent(GTM_EVENT_NAME.CONNECTION_INCOMPLETE, {
                                id: connectionId || '',
                                connector_type:
                                    datastreamTypeId || connectionTypeId || '',
                                error_code: code || '',
                                error_message:
                                    errorMessage.locale ||
                                    errorMessage.message ||
                                    errorMessage.customFallback ||
                                    '',
                            });
                        }

                        const helpUrl = this.helpUrl[this.userLang];

                        this.renderFailNoti({
                            ...errorMessage,
                            customFallback:
                                this.page === FORM_CONNECTION
                                    ? helpUrl
                                        ? this.$t(
                                              'CONNECTION.form_auth_fail_noti',
                                              {
                                                  link: helpUrl,
                                              }
                                          )
                                        : this.$t('CONNECTION.oauth_fail_noti')
                                    : this.$t(
                                          'CONNECTION.datasource_creation_fail_noti'
                                      ),
                        });
                    } else {
                        const errorMessage = this.handleErrorMessage(e);
                        return this.renderFailNoti(errorMessage);
                    }
                })
                .finally(() => {
                    this.loadingMask = false;
                });
        },
        onClickConfirm() {
            const options = this.refineFormToParam();
            console.log(options, 'options');
            return this.submitForm(options);
        },
        refreshAdAccounts() {
            this.$emit('refresh', { token: this.token });
        },
        renderNoAccessDialog({ isClosing }) {
            return this.dialog({
                hasCancelButton: false,
                child: (
                    <div class="connection__dialog__no-access">
                        <S.DialogText
                            domPropsInnerHTML={
                                this.haveAccess
                                    ? this.$t(
                                          'CONNECTION.dashboard_message_make_connection'
                                      )
                                    : this.$t(
                                          'CONNECTION.dashboard_message_make_connection_non_whitelisted'
                                      )
                            }
                        ></S.DialogText>
                        <a onClick={() => this.showHubspotMsg()}>
                            {this.$t('COMMON.contact_adriel')}
                        </a>
                    </div>
                ),
                confirmCb: () => {
                    isClosing && this.onClose();
                },
            });
        },
        unpaidSubMessage() {
            return (
                <S.AnchorText slot="info">
                    {this.language === 'ko'
                        ? [
                              `본 기능은 프리미엄 서비스입니다. 아래 계정 정보를 입력해주세요. 도움이 필요하다면 `,
                              <a onClick={() => this.showHubspotMsg()}>
                                  전문 컨설턴트에게 문의해주세요.
                              </a>,
                          ]
                        : [
                              `This data source is a premium connector. Please add your credentials below and `,
                              <a onClick={() => this.showHubspotMsg()}>
                                  get in touch
                              </a>,
                              ` with an onboarding specialist to establish a premium data stream.`,
                          ]}
                </S.AnchorText>
            );
        },
        setStreamData() {
            const {
                id,
                connectionTypesIds,
                haveAccess,
                forceNewConnection,
                connectionId,
                isPremium,
            } = this.streamItem;

            this.datastreamTypeId = id;
            this.connectionTypesIds = connectionTypesIds;
            this.haveAccess = haveAccess;
            this.isPremium = Boolean(isPremium);

            if (connectionId) {
                this.onRefreshConnection({
                    connectionTypeId: R.head(connectionTypesIds),
                    connectionId,
                });
            } else {
                this.page = CONNECTIONS;
                this.onClickDatastreamType({
                    connectionTypesIds,
                    forceNewConnection,
                });
            }
        },
        initPage() {
            if (this.subscriptionWarningVisible) {
                this.page = CONNECTIONS;
                return;
            }

            if (this.isConnectionPage) {
                this.setStreamData();
            } else if (this.isQuickConnect) {
                this.observeAccountToken$();
            } else {
                this.page = DATASTREAM_TYPES;
            }
        },
        observeAccountToken$() {
            const { generateWatchNew$: gen$ } = this;

            const isQuickConnect$ = gen$('isQuickConnect').pipe(
                filter(R.identity),
                switchMap(() => gen$('token').pipe(filter(R.identity)))
            );

            this.$subscribeTo(isQuickConnect$, () => {
                this.setStreamData();
            });
        },
        renderFileUploadPanel() {
            const props = {
                attrs: {
                    wrapStyle: { zIndex: 13 },
                    active: this.fileConnectorPanelOpened,
                    headerText: this.$t('CONNECTION.upload_no_file_main_title'),
                    onCancel: () => {
                        this.onClose();
                    },
                },
            };
            return (
                <Drawer {...props}>
                    <S.FileConnectorGuide>
                        {this.$t('CONNECTION.upload_second_panel_description')}
                    </S.FileConnectorGuide>
                    <S.FileConnectorSubGuide>
                        {this.$t(
                            getUploadCopyKeys(
                                getUploadableFileTypes(this.generatedAssetType)
                            ).guide
                        )}
                    </S.FileConnectorSubGuide>
                    <FileUploadConnector
                        assetId={this.generatedAssetId}
                        assetType={this.generatedAssetType}
                    />
                </Drawer>
            );
        },
        setPredefinedSearchTerm(value) {
            if (value) {
                this.setSearchTerm(value);
            }
        },
        async handleFileUpload(file, fieldName, fileType = []) {
            if (!file) return;

            // 파일 확장자 검증
            const fileName = file.name.toLowerCase();
            const fileExtension = '.' + fileName.split('.').pop();

            if (fileType.length > 0 && !fileType.includes(fileExtension)) {
                this.renderFailNoti({
                    locale: `${this.$t(
                        'COMMON.invalid_file_type'
                    )}: ${fileType.join(', ')}`,
                });

                return;
            }

            // 기존 파일이 있다면 제거 (하나의 파일만 허용)
            const index = R.findIndex(
                R.propEq('name', fieldName),
                this.formData
            );
            if (index !== -1 && this.formData[index].value) {
                // TODO: 기존 파일 삭제 API 호출 (추후 구현)
                console.log(
                    'Removing existing file:',
                    this.formData[index].value
                );
            }

            // 로딩 시작
            this.$set(this.fileUploadLoading, fieldName, true);

            try {
                const response = await UploadAPI.submitAssetMedia(
                    'media',
                    file
                );

                if (response && response.length > 0) {
                    const uploadedUrl = response[0].path;

                    // form data 업데이트
                    if (index !== -1) {
                        this.formData = R.assocPath(
                            [index, 'value'],
                            uploadedUrl,
                            this.formData
                        );
                    }
                } else {
                    this.renderFailNoti({
                        locale: this.$t('CONNECTION.file_upload_failed'),
                    });
                }
            } catch (error) {
                const errorMessage = this.handleErrorMessage(error);
                this.renderFailNoti({
                    ...errorMessage,
                    customFallback: this.$t('CONNECTION.file_upload_failed'),
                });
            } finally {
                this.$set(this.fileUploadLoading, fieldName, false);
            }
        },
        removeUploadedFile(fieldName) {
            // TODO: 추후 delete API 추가시 연결

            // form data에서 값 제거
            const index = R.findIndex(
                R.propEq('name', fieldName),
                this.formData
            );
            if (index !== -1) {
                this.formData = R.assocPath(
                    [index, 'value'],
                    '',
                    this.formData
                );
            }

            // file input 리셋을 위해 key 변경
            this.fileInputKeys = {
                ...this.fileInputKeys,
                [fieldName]: (this.fileInputKeys[fieldName] || 0) + 1,
            };
        },
        renderCustomFileldPanel() {
            if (!this.customFieldPanelOpend) return;

            const { value = '', onInput = R.identity } =
                this.customFieldPanelProps;

            const props = {
                attrs: {
                    active: this.customFieldPanelOpend,
                    customCode: value,
                    origin: 'connection',
                },
                on: {
                    activeChange: (v) => (this.customFieldPanelOpend = v),
                    onInputCustomCode: onInput,
                },
            };
            return <CustomFieldPanel {...props} />;
        },
        async handleCheckingAuthConnection(params = {}) {
            try {
                const data = await AssetService.addAuthConnection(params);

                return data || {};
            } catch (error) {
                const data = R.pathOr({}, ['response', 'data', 'error'], error);

                const refinedError = R.assocPath(
                    ['response', 'data'],
                    data,
                    error
                );

                throw refinedError;
            }
        },
        handleErrorMessage(error) {
            console.error(error);

            const { message = '', code = '', traceId = '' } = R.pathOr(
                {},
                ['response', 'data'],
                error
            );

            const localeExistCode = `WIDGET.error_panel.error.${code}`;
            const translatedMessage = this.$t(localeExistCode);

            return {
                locale: this.$te(localeExistCode) ? translatedMessage : '',
                code,
                message,
                traceId,
            };
        },
        getChoiceDisplayName(value) {
            if (this.language === 'ko') {
                return value?.display_name_kr || value?.display_name;
            }
            if (this.language === 'ja') {
                return value?.display_name_jp || value?.display_name;
            }
            return value?.display_name;
        },
    },
    created() {
        this.hideChatbot();

        const toTop$ = document.querySelector('.to-top-wrapper');

        if (toTop$) {
            toTop$.style.display = 'none';
        }

        this.clipHtml(true);
    },
    mounted() {
        this.$nextTick(() => {
            this.observeSearchTerm$();
            this.initPage();
            this.setPredefinedSearchTerm();
        });

        if (!isValueExist(this.assets)) {
            this.fetchAssets({
                token: this.isAdmin
                    ? R.pathOr(
                          this.accountToken,
                          ['$route', 'query', 'token'],
                          this
                      )
                    : this.accountToken,
                accountId: this.accountId,
            });
        }
    },
    beforeDestroy() {
        this.loadChatbot();
        this.removeEventFn();

        const toTop$ = document.querySelector('.to-top-wrapper');

        if (toTop$) {
            toTop$.style.display = 'flex';
        }

        this.clipHtml(false);

        this.setNotiMsgs([]);
    },
    render() {
        const {
            active,
            renderHeader,
            renderPage,
            page,
            onClickConfirm,
            loadingMask,
            checkIsValid,
            renderModals,
            closeOutside,
            width,
            fileConnectorPanelOpened,
            placement,
            bodyStyle,
            isFromAdgen,
            isUnblockPage,
        } = this;

        const props = {
            attrs: {
                active,
                editAreaPadding: '20px 28px',
                closeOutside,
                hasButtons:
                    page === FORM_CONNECTION || page === FORM_DATASTREAM,
                onConfirm: onClickConfirm,
                loadingMask,
                loadingMaskAdditionalUI: this.isOAuthLoading
                    ? 
                    (<S.AdditionalLoadingWrapper>
                          <LoadingMaskText>
                              {this.$t('CONNECTION.oauth_waiting_text')}
                          </LoadingMaskText>
                          <ActionButton
                              type="cancel"
                              onClick={this.handleOAuthCancel}
                          >
                              {this.$t('COMMON.cancel')}
                          </ActionButton>
                    </S.AdditionalLoadingWrapper>)
                      
                    : null,
                confirmDisabled: !checkIsValid || fileConnectorPanelOpened,
                onCancel: this.onClose,
                closeBlockClasses: [
                    'adriel-dialog',
                    'adriel-tooltip',
                    'ant-tooltip',
                    'notification-close-icon',
                    'see-more-wrapper',
                    'notification-box',
                ],
                confirmText: this.$t('COMMON.submit'),
                zIndex: 13,
                width,
                mask: this.isQuickConnect,
                hasClose: !this.isQuickConnect,
                hasCancel: !this.isQuickConnect,
                placement,
                bodyStyle,
                unblock: isFromAdgen || isUnblockPage,
            },
        };

        return (
            <div>
                <Drawer {...props}>
                    {renderHeader()}
                    {!this.isQuickConnect && isValueExist(this.helpUrl) && (
                        <S.GuideWrapper
                            onClick={() => {
                                // docs.adriel.com blocks iframe embedding
                                // (X-Frame-Options: DENY), so open in a new tab
                                window.open(
                                    this.helpUrl[this.language] ||
                                        this.helpUrl.en,
                                    '_blank',
                                    'noopener,noreferrer'
                                );
                            }}
                        >
                            <img
                                src={
                                    this.language === 'ko'
                                        ? '/static/assets/img/button-connection-guide-kr.png'
                                        : '/static/assets/img/button-connection-guide.png'
                                }
                            />
                        </S.GuideWrapper>
                    )}
                    {this.page === FORM_CONNECTION &&
                        !this.haveAccess &&
                        this.unpaidSubMessage()}
                    {renderPage()}
                    <portal to="panel_modal">{renderModals()}</portal>
                </Drawer>
                {this.renderFileUploadPanel()}
                {this.renderCustomFileldPanel()}
            </div>
        );
    },
};

export default withHMR(
    module,
    DashboardConnectionPanel,
    DashboardConnectionPanel.name
);
